/**
 * Shared modal open/close animation helpers.
 *
 * Timing must stay in sync with shared/scss/_modal-animation.scss (300ms).
 */

export const MODAL_OPEN_CLASS = 'is-open';
export const MODAL_ANIMATION_MS = 300;

/**
 * Reset scroll on a modal root and every descendant that can scroll.
 *
 * Call this after content is populated and the modal is shown, so a previous
 * open does not restore scrollTop on the same container.
 *
 * @param {HTMLElement} root Modal root or dialog.
 */
export function resetModalScroll( root ) {
	if ( ! root ) {
		return;
	}

	const reset = ( element ) => {
		element.scrollTop = 0;
		element.scrollLeft = 0;
	};

	reset( root );
	root.querySelectorAll( '*' ).forEach( reset );
}

/**
 * @return {boolean} Whether the user prefers reduced motion.
 */
export function prefersReducedMotion() {
	return (
		typeof window.matchMedia === 'function' &&
		window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
	);
}

/**
 * Play the enter animation after the modal is shown (hidden removed).
 *
 * Forces a closed-state paint before adding the open class so CSS can animate.
 *
 * @param {HTMLElement} modal               Modal root element.
 * @param {Object}      [options]           Options.
 * @param {string}      [options.openClass] Open state class.
 * @param {Function}    [options.onOpened]  Called after the open class is applied.
 */
export function animateModalOpen(
	modal,
	{ openClass = MODAL_OPEN_CLASS, onOpened } = {}
) {
	if ( ! modal ) {
		return;
	}

	// Reset before the first paint so the closed state is already at the top.
	resetModalScroll( modal );

	// Paint the closed visual state, then add is-open so CSS can animate.
	void modal.offsetWidth;

	window.requestAnimationFrame( () => {
		window.requestAnimationFrame( () => {
			resetModalScroll( modal );
			modal.classList.add( openClass );
			resetModalScroll( modal );

			if ( typeof onOpened === 'function' ) {
				onOpened();
			}
		} );
	} );
}

/**
 * Remove the open class and invoke onClosed after the leave transition.
 *
 * @param {HTMLElement} modal               Modal root element.
 * @param {Object}      [options]           Options.
 * @param {string}      [options.openClass] Open state class.
 * @param {Function}    [options.onClosed]  Called after animation completes (or immediately when reduced motion).
 */
export function animateModalClose(
	modal,
	{ openClass = MODAL_OPEN_CLASS, onClosed } = {}
) {
	if ( ! modal ) {
		if ( typeof onClosed === 'function' ) {
			onClosed();
		}
		return;
	}

	if ( ! modal.classList.contains( openClass ) ) {
		if ( typeof onClosed === 'function' ) {
			onClosed();
		}
		return;
	}

	modal.classList.remove( openClass );

	let settled = false;
	let closeFallbackTimer = 0;

	/**
	 * Finalize close once.
	 */
	function finishClose() {
		if ( settled ) {
			return;
		}

		settled = true;
		modal.removeEventListener( 'transitionend', onTransitionEnd );
		window.clearTimeout( closeFallbackTimer );

		if ( typeof onClosed === 'function' ) {
			onClosed();
		}
	}

	/**
	 * @param {TransitionEvent} event Transition event.
	 */
	function onTransitionEnd( event ) {
		if ( event.target !== modal ) {
			return;
		}

		if ( event.propertyName !== 'opacity' ) {
			return;
		}

		finishClose();
	}

	if ( prefersReducedMotion() ) {
		finishClose();
		return;
	}

	modal.addEventListener( 'transitionend', onTransitionEnd );
	closeFallbackTimer = window.setTimeout(
		finishClose,
		MODAL_ANIMATION_MS + 50
	);
}
